import { getBrandConfig } from '@/utils/brand';
import rollbar from '@/utils/rollbar';

const brandConfig = getBrandConfig();

export enum SpreadType {
  Fixed = 'FIXED',
  Percentage = 'PERCENTAGE',
}

export enum Currency {
  USDT = 'USDT',
  USD = 'USD',
  BRL = 'BRL',
}

export enum ExchangeService {
  Awesome = 'AWESOME',
  Bitso = 'BITSO',
  BlockRiver = 'BLOCK_RIVER',
  OperatorOne = 'TRADING_VIEW', // Kept for compatibility, but should be renamed to OPERATOR_1
  OperatorTwo = 'OPERATOR_2',
  ENor = 'ENOR',
  Greenlabs = 'GREENLABS',
  ExchangeNow = 'EXCHANGE_NOW',
}

export type ExchangeCurrencyPair = {
  service: ExchangeService;
  currency: Currency;
};

export type ExchangeLimits = {
  minAmount?: number;
  maxAmount?: number;
};

export const EXCHANGE_LIMITS: Record<ExchangeService, ExchangeLimits> = {
  [ExchangeService.Awesome]: {},
  [ExchangeService.Bitso]: {},
  [ExchangeService.BlockRiver]: {},
  [ExchangeService.OperatorOne]: {},
  [ExchangeService.OperatorTwo]: {},
  [ExchangeService.ENor]: {},
  [ExchangeService.Greenlabs]: {
    minAmount: 10000,
    maxAmount: 500000,
  },
  [ExchangeService.ExchangeNow]: {
    minAmount: 100,
  },
};

export function getExchangeLimits(service: ExchangeService): ExchangeLimits {
  return EXCHANGE_LIMITS[service] || {};
}

export function requiresWalletAddressInput(currency: Currency): boolean {
  return currency === Currency.USDT;
}

// Active exchanges sorted by priority
export const EXCHANGES: ExchangeService[] = [
  ExchangeService.OperatorOne,
  ExchangeService.Greenlabs,
  ExchangeService.BlockRiver,
  ExchangeService.OperatorTwo,
  ExchangeService.ENor,
  ExchangeService.Awesome,
  ExchangeService.ExchangeNow,
];

// Supported currencies for orders
export const CURRENCIES: Currency[] = [Currency.USDT, Currency.USD];

// Exchange enabled currency mapping
export const EXCHANGE_SUPPORTED_CURRENCIES: Record<ExchangeService, Currency[]> = {
  [ExchangeService.Awesome]: [Currency.USDT],
  [ExchangeService.Bitso]: [Currency.USDT],
  [ExchangeService.BlockRiver]: [Currency.USDT],
  [ExchangeService.OperatorOne]: [Currency.USDT, Currency.USD],
  [ExchangeService.OperatorTwo]: [Currency.USDT],
  [ExchangeService.ENor]: [Currency.USDT],
  [ExchangeService.Greenlabs]: [Currency.USDT],
  [ExchangeService.ExchangeNow]: [Currency.USDT],
};

const EXCHANGE_SERVICE_LABELS: Record<ExchangeService, string> = {
  [ExchangeService.Awesome]: 'Awesome',
  [ExchangeService.Bitso]: 'Bitso Exchange',
  [ExchangeService.BlockRiver]: 'Block River',
  [ExchangeService.OperatorOne]: 'Operadora 1',
  [ExchangeService.OperatorTwo]: 'Operadora 2',
  [ExchangeService.ENor]: 'eNor',
  [ExchangeService.Greenlabs]: 'Greenlabs',
  [ExchangeService.ExchangeNow]: 'Exchange Now',
};

const EXCHANGE_SERVICE_OBFUSCATED_LABELS: Record<ExchangeService, string> = {
  [ExchangeService.Awesome]: 'Operadora 7',
  [ExchangeService.Bitso]: 'Operadora 6',
  [ExchangeService.BlockRiver]: 'Operadora 4',
  [ExchangeService.OperatorOne]: 'Operadora 1',
  [ExchangeService.OperatorTwo]: 'Operadora 2',
  [ExchangeService.ENor]: 'Operadora 5',
  [ExchangeService.Greenlabs]: 'Operadora 3',
  [ExchangeService.ExchangeNow]: 'Operadora 8',
};

export const EXCHANGE_SERVICE_OPTIONS = CURRENCIES.reduce(
  (acc, currency) => {
    acc[currency] = EXCHANGES.filter((exchange) =>
      EXCHANGE_SUPPORTED_CURRENCIES[exchange].includes(currency),
    ).map((exchange) => ({
      value: exchange,
      label: brandConfig.features.hideExchangeName
        ? EXCHANGE_SERVICE_OBFUSCATED_LABELS[exchange]
        : EXCHANGE_SERVICE_LABELS[exchange],
    }));

    return acc;
  },
  {} as Record<Currency, { value: ExchangeService; label: string }[]>,
);

export function getExchangeServiceLabel(service: ExchangeService, strict = false): string {
  const label = brandConfig.features.hideExchangeName
    ? EXCHANGE_SERVICE_OBFUSCATED_LABELS[service]
    : EXCHANGE_SERVICE_LABELS[service];
  if (!label && strict) {
    rollbar.error('Exchange service label not found', { service });
  }

  if (!label && brandConfig.features.hideExchangeName) {
    return 'Operadora não identificada';
  }

  return label || service;
}

export function getNonObfuscatedExchangeServiceLabel(service: ExchangeService): string {
  return EXCHANGE_SERVICE_LABELS[service] || service;
}

const CURRENCY_LABELS: Record<Currency, string> = {
  [Currency.USDT]: 'Tether (USDT)',
  [Currency.USD]: 'US Dollar (USD)',
  [Currency.BRL]: 'Real (BRL)',
};

const CURRENCY_LABELS_COMPACT: Record<Currency, string> = {
  [Currency.USDT]: 'USDT',
  [Currency.USD]: 'USD',
  [Currency.BRL]: 'BRL',
};

export function getCurrencyLabel(currency: Currency): string {
  return CURRENCY_LABELS[currency] || currency;
}

export function getCurrencyLabelCompact(currency: Currency): string {
  return CURRENCY_LABELS_COMPACT[currency] || currency;
}

const SPREAD_TYPE_LABELS: Record<SpreadType, string> = {
  [SpreadType.Fixed]: 'Fixo',
  [SpreadType.Percentage]: 'Percentual',
};

export const SPREAD_TYPE_OPTIONS = Object.entries(SPREAD_TYPE_LABELS).map(([key, value]) => ({
  value: key as SpreadType,
  label: value,
}));

export function getUserSpreadTypeLabel(type: SpreadType): string {
  return SPREAD_TYPE_LABELS[type] || type;
}
